import { useEffect, useRef, useState } from 'react'
import {
    NavLink,
    Navigate,
    Outlet,
    useLocation,
    useNavigate,
} from 'react-router-dom'
import { signOut } from '../api/auth'
import { API_BASE } from '../api/client'
import { useCreateWorkspace } from '../hooks/useApi'
import { useT } from '../i18n/context'
import type { DictPath } from '../i18n/translate'
import { errText } from '../lib/errors'
import { entryTypeMeta } from '../lib/labelMaps'
import { formatClock } from '../lib/format'
import { useAuthStore } from '../stores/auth'
import { useTimerStore } from '../stores/timer'
import { useActiveWorkspaceId, useWorkspaceStore } from '../stores/workspace'
import { toast } from '../stores/toast'
import { APP_BUILD, APP_VERSION } from '../version'
import { BetaBadge } from './BetaBadge'
import { FeedbackDialog } from './FeedbackDialog'
import { Footer } from './Footer'
import { LanguageToggle } from './LanguageToggle'
import { ThemeToggle } from './ThemeToggle'
import { Button } from './ui/Button'
import { Field, Input } from './ui/Field'
import { PageLoader } from './ui/Spinner'

interface NavChild {
    to: string
    labelKey: DictPath
}

interface NavGroup {
    to: string
    labelKey: DictPath
    end?: boolean
    /** Dezente Dauer-Betonung (Kernfunktion „Zeiterfassung“). */
    highlight?: boolean
    /** Sub-Routen — nur im Mobile-Menü als Direktlinks sichtbar. */
    children?: NavChild[]
}

const navGroups: NavGroup[] = [
    { to: '/track', labelKey: 'nav.tracking', end: true, highlight: true },
    {
        to: '/billing',
        labelKey: 'nav.billing',
        children: [
            { to: '/billing/entries', labelKey: 'nav.entries' },
            { to: '/billing/reports', labelKey: 'nav.reports' },
            { to: '/billing/invoices', labelKey: 'nav.invoices' },
            { to: '/billing/monthly', labelKey: 'nav.monthly' },
        ],
    },
    {
        to: '/workspace',
        labelKey: 'nav.workspace',
        children: [
            { to: '/workspace/clients', labelKey: 'workspace.tabClients' },
            { to: '/workspace/members', labelKey: 'workspace.tabMembers' },
            { to: '/workspace/addresses', labelKey: 'workspace.tabAddresses' },
            { to: '/workspace/general', labelKey: 'workspace.tabGeneral' },
        ],
    },
]

export function RequireAuth({ children }: { children: React.ReactNode }) {
    const status = useAuthStore(s => s.status)
    if (status === 'boot') return <PageLoader />
    if (status === 'unauthenticated') return <Navigate to='/login' replace />
    return <>{children}</>
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
    const status = useAuthStore(s => s.status)
    const user = useAuthStore(s => s.user)
    if (status === 'boot') return <PageLoader />
    if (!user?.isAdmin) return <Navigate to='/track' replace />
    return <>{children}</>
}

export function AppLayout() {
    const t = useT()
    const user = useAuthStore(s => s.user)
    const workspaceId = useActiveWorkspaceId()
    const [menuOpen, setMenuOpen] = useState(false)
    const [feedbackOpen, setFeedbackOpen] = useState(false)
    const items: NavGroup[] = user?.isAdmin
        ? [...navGroups, { to: '/admin', labelKey: 'nav.admin' }]
        : navGroups

    // Laufenden Timer beim Wechsel des Workspace nachladen
    const loadRunning = useTimerStore(s => s.loadRunning)
    useEffect(() => {
        if (workspaceId !== null) void loadRunning(workspaceId).catch(() => undefined)
    }, [workspaceId, loadRunning])

    // Mobile-Menü beim Routenwechsel schließen (z. B. Browser-Back) —
    // State-Reset während Render statt Effect (initializedFor-Muster)
    const location = useLocation()
    const [lastPath, setLastPath] = useState(location.pathname)
    if (lastPath !== location.pathname) {
        setLastPath(location.pathname)
        if (menuOpen) setMenuOpen(false)
    }

    usePageviews()

    return (
        <div className='flex min-h-screen flex-col'>
            <header className='sticky top-0 z-40 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'>
                <div className='mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:gap-4'>
                    {/* Brand-Marke — klickbar, führt zur Zeiterfassung */}
                    {/* Kein aria-label: Der Name kommt aus dem sichtbaren Text
                        („Optimus Time" + Beta-Badge), sonst meldet axe einen
                        label-content-name-mismatch. */}
                    <NavLink
                        to='/track'
                        className='flex items-center gap-2 text-base font-bold tracking-tight text-indigo-800 dark:text-indigo-300'
                    >
                        <span className='flex h-8 w-8 items-center justify-center rounded-[10px] bg-indigo-600 dark:bg-indigo-500 text-white'>
                            <svg
                                className='h-5 w-5'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2.4}
                                strokeLinecap='round'
                                aria-hidden='true'
                            >
                                <circle cx='12' cy='12' r='9' />
                                <path d='M12 7v5l3.5 2' />
                            </svg>
                        </span>
                        Optimus Time
                        <BetaBadge />
                    </NavLink>
                    <nav className='hidden items-center gap-1 sm:flex'>
                        {items.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300'
                                            : item.highlight
                                              ? 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`
                                }
                            >
                                {t(item.labelKey)}
                            </NavLink>
                        ))}
                    </nav>
                    <div className='ml-auto flex items-center gap-2 sm:gap-3'>
                        <NavTimer />
                        {user && user.workspaces.length > 0 && (
                            <div className='hidden sm:block'>
                                <WorkspaceSwitcher />
                            </div>
                        )}
                        <UserMenu onFeedback={() => setFeedbackOpen(true)} />
                        <HamburgerButton
                            open={menuOpen}
                            onToggle={() => setMenuOpen(open => !open)}
                        />
                    </div>
                </div>
                {menuOpen && (
                    <MobileMenu items={items} onClose={() => setMenuOpen(false)} />
                )}
            </header>

            <main className='mx-auto w-full max-w-6xl flex-1 px-4 py-6'>
                {workspaceId === null ? <WorkspaceOnboarding /> : <Outlet />}
            </main>
            <Footer />
            <FeedbackDialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
            />
        </div>
    )
}

/** Hamburger für < sm — zwischen offen/geschlossen wechselndes Icon. */
function HamburgerButton({
    open,
    onToggle,
}: {
    open: boolean
    onToggle: () => void
}) {
    const t = useT()
    return (
        <button
            type='button'
            className='flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 sm:hidden'
            aria-expanded={open}
            aria-controls='mobile-menu'
            aria-label={open ? t('common.closeMenu') : t('common.openMenu')}
            onClick={onToggle}
        >
            <svg
                className='h-5 w-5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
                aria-hidden='true'
            >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d={open ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
            </svg>
        </button>
    )
}

/**
 * Zahnrad-Menü in der Topbar: bündelt Einstellungen, Sprache, Design und
 * Abmelden (samt User-Info) — Nav bleibt bei den 3 Gruppen.
 */
function UserMenu({ onFeedback }: { onFeedback: () => void }) {
    const t = useT()
    const user = useAuthStore(s => s.user)
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    // Klick außerhalb und Escape schließen das Menü
    useEffect(() => {
        if (!open) return
        const onPointerDown = (e: PointerEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node))
                setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    // Bei Routenwechsel schließen (z. B. Browser-Back) — State-Reset
    // während Render statt Effect (initializedFor-Muster)
    const location = useLocation()
    const [lastPath, setLastPath] = useState(location.pathname)
    if (lastPath !== location.pathname) {
        setLastPath(location.pathname)
        if (open) setOpen(false)
    }

    return (
        <div ref={ref} className='relative'>
            <button
                type='button'
                onClick={() => setOpen(o => !o)}
                aria-haspopup='menu'
                aria-expanded={open}
                aria-label={t('nav.userMenu')}
                title={t('nav.userMenu')}
                className='flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800'
            >
                <svg
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    strokeWidth={2}
                    aria-hidden='true'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z'
                    />
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
                    />
                </svg>
            </button>

            {open && (
                <div
                    role='menu'
                    className='absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1.5 shadow-lg'
                >
                    {user && (
                        <div className='flex items-center gap-2.5 border-b border-slate-200 dark:border-slate-700 px-2.5 pb-2.5 pt-1.5'>
                            <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300'>
                                {user.name?.charAt(0).toUpperCase() ?? '?'}
                            </span>
                            <span className='min-w-0'>
                                <span className='block truncate text-sm font-semibold text-slate-800 dark:text-slate-100'>
                                    {user.name}
                                </span>
                                <span
                                    className='block truncate text-xs text-slate-500 dark:text-slate-400'
                                    title={user.email}
                                >
                                    {user.email}
                                </span>
                            </span>
                        </div>
                    )}
                    <NavLink
                        to='/settings'
                        role='menuitem'
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                            `mt-1 block rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                                isActive
                                    ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300'
                                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`
                        }
                    >
                        {t('nav.settings')}
                    </NavLink>
                    <div className='flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5'>
                        <span className='text-sm text-slate-600 dark:text-slate-300'>
                            {t('nav.language')}
                        </span>
                        <LanguageToggle />
                    </div>
                    <div className='flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5'>
                        <span className='text-sm text-slate-600 dark:text-slate-300'>
                            {t('theme.label')}
                        </span>
                        <ThemeToggle />
                    </div>
                    <button
                        type='button'
                        role='menuitem'
                        onClick={() => {
                            setOpen(false)
                            onFeedback()
                        }}
                        className='mt-1 block w-full rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800'
                    >
                        {t('nav.feedback')}
                    </button>
                    <button
                        type='button'
                        role='menuitem'
                        onClick={() => void signOut()}
                        className='mt-1 block w-full rounded-lg px-2.5 py-2 text-left text-sm font-medium text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40'
                    >
                        {t('nav.logout')}
                    </button>
                    {/* Build-Stand — passive Infozeile, kein Menüitem */}
                    <div className='mt-1 border-t border-slate-200 dark:border-slate-700 px-2.5 pb-1 pt-2 text-center'>
                        <span className='text-[11px] tabular-nums text-slate-500 dark:text-slate-400'>
                            v{APP_VERSION} · {APP_BUILD}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

/** Mobile-Navigation: Slide-down-Panel unter dem Sticky-Header, mit Backdrop. */
function MobileMenu({ items, onClose }: { items: NavGroup[]; onClose: () => void }) {
    const t = useT()
    const user = useAuthStore(s => s.user)

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    return (
        <>
            <div
                className='fixed inset-x-0 bottom-0 top-14 z-30 bg-slate-900/30 sm:hidden'
                onClick={onClose}
                aria-hidden='true'
            />
            <nav
                id='mobile-menu'
                aria-label={t('nav.mainNav')}
                className='absolute inset-x-0 top-full z-40 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 shadow-lg sm:hidden'
            >
                <MobileTimerRow onClose={onClose} />
                <div className='flex flex-col gap-0.5'>
                    {items.map(item => (
                        <div key={item.to}>
                            <NavLink
                                to={item.to}
                                end={item.end}
                                onClick={onClose}
                                className={({ isActive }) =>
                                    `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300'
                                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`
                                }
                            >
                                {t(item.labelKey)}
                            </NavLink>
                            {item.children && (
                                <div className='ml-3.5 flex flex-col gap-0.5 border-l border-slate-200 dark:border-slate-700 pl-2.5'>
                                    {item.children.map(child => (
                                        <NavLink
                                            key={child.to}
                                            to={child.to}
                                            onClick={onClose}
                                            className={({ isActive }) =>
                                                `block rounded-lg px-3 py-1.5 text-sm transition-colors ${
                                                    isActive
                                                        ? 'font-medium text-indigo-800 dark:text-indigo-300'
                                                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`
                                            }
                                        >
                                            {t(child.labelKey)}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {user && user.workspaces.length > 0 && (
                    <div className='mt-3 border-t border-slate-200 dark:border-slate-700 pt-3'>
                        <WorkspaceSwitcher className='w-full' />
                    </div>
                )}
            </nav>
        </>
    )
}

/**
 * Laufender Timer in der Topbar: Klick auf Uhr/Typ springt zur Zeiterfassung,
 * der Stopp-Button beendet den Timer direkt aus der Navigation.
 */
function NavTimer() {
    const t = useT()
    const navigate = useNavigate()
    const running = useTimerStore(s => s.running)
    const elapsed = useTimerStore(s => s.elapsed)
    const syncing = useTimerStore(s => s.syncing)
    const stop = useTimerStore(s => s.stop)
    if (!running) return null

    const handleStop = async (e: React.MouseEvent) => {
        e.stopPropagation()
        try {
            await stop()
            toast.success(t('timer.stopped'))
        } catch (err) {
            toast.error(errText(err))
        }
    }

    const typeLabel = t(entryTypeMeta[running.entryType].labelKey)
    const tooltip = `${t('timer.goToTracking')} — ${typeLabel}${
        running.description ? `: ${running.description}` : ''
    }`

    return (
        <div
            className='flex shrink-0 items-center gap-0.5 rounded-full bg-red-50 dark:bg-red-950/60 py-0.5 pl-2.5 pr-1 text-xs font-semibold text-red-700 dark:text-red-300 shadow-sm'
            data-running-timer
        >
            <button
                type='button'
                onClick={() => navigate('/track')}
                title={tooltip}
                className='flex min-w-0 items-center gap-2 rounded-full py-1 pr-1 transition-colors hover:bg-red-100/70 dark:hover:bg-red-900/50'
            >
                <span className='h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500' />
                <span className='tabular-nums'>{formatClock(elapsed)}</span>
                <span className='hidden font-medium text-red-600/80 dark:text-red-300/80 sm:inline'>
                    {typeLabel}
                </span>
                {running.description && (
                    <span className='hidden max-w-28 truncate font-normal text-red-600/70 dark:text-red-300/70 lg:inline'>
                        {running.description}
                    </span>
                )}
            </button>
            <StopButton syncing={syncing} onStop={handleStop} />
        </div>
    )
}

/** Timer-Zeile im Mobile-Menü: Klick springt zur Zeiterfassung, Stopp beendet. */
function MobileTimerRow({ onClose }: { onClose: () => void }) {
    const t = useT()
    const navigate = useNavigate()
    const running = useTimerStore(s => s.running)
    const elapsed = useTimerStore(s => s.elapsed)
    const syncing = useTimerStore(s => s.syncing)
    const stop = useTimerStore(s => s.stop)
    if (!running) return null

    const handleStop = async () => {
        try {
            await stop()
            toast.success(t('timer.stopped'))
        } catch (err) {
            toast.error(errText(err))
        }
    }

    return (
        <div className='mb-2 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/60 py-1.5 pl-3 pr-1.5 text-xs font-semibold text-red-700 dark:text-red-300'>
            <button
                type='button'
                onClick={() => {
                    onClose()
                    navigate('/track')
                }}
                className='flex min-w-0 flex-1 items-center gap-2 text-left'
                title={t('timer.goToTracking')}
            >
                <span className='h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500' />
                <span className='shrink-0 tabular-nums'>
                    {formatClock(elapsed)}
                </span>
                <span className='shrink-0 font-medium text-red-600/80 dark:text-red-300/80'>
                    {t(entryTypeMeta[running.entryType].labelKey)}
                </span>
                {running.description && (
                    <span className='min-w-0 flex-1 truncate font-normal text-red-600/70 dark:text-red-300/70'>
                        {running.description}
                    </span>
                )}
            </button>
            <StopButton syncing={syncing} onStop={() => void handleStop()} />
        </div>
    )
}

/** Quadrat-Icon-Button zum Stoppen des laufenden Timers. */
function StopButton({
    syncing,
    onStop,
}: {
    syncing: boolean
    onStop: (e: React.MouseEvent) => void
}) {
    const t = useT()
    return (
        <button
            type='button'
            onClick={onStop}
            disabled={syncing}
            aria-label={t('timer.stop')}
            title={t('timer.stop')}
            className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-red-200/70 dark:hover:bg-red-800/60 disabled:cursor-not-allowed disabled:opacity-50'
        >
            <svg
                className='h-3 w-3'
                viewBox='0 0 24 24'
                fill='currentColor'
                aria-hidden='true'
            >
                <rect x='6' y='6' width='12' height='12' rx='2' />
            </svg>
        </button>
    )
}

function WorkspaceSwitcher({ className = 'max-w-44' }: { className?: string }) {
    const t = useT()
    const user = useAuthStore(s => s.user)
    const workspaceId = useActiveWorkspaceId()
    const setWorkspace = useWorkspaceStore(s => s.setWorkspace)
    return (
        <select
            value={workspaceId ?? undefined}
            onChange={e => setWorkspace(Number(e.target.value))}
            aria-label={t('nav.workspaceSwitch')}
            className={`${className} select-caret cursor-pointer rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 transition-colors hover:border-slate-400 dark:hover:border-slate-500 focus:border-indigo-600 focus:outline-none`}
        >
            {user!.workspaces.map(({ workspace }) => (
                <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                </option>
            ))}
        </select>
    )
}

/** Signup legt keinen Workspace an — deshalb Onboarding für frische Accounts. */
function WorkspaceOnboarding() {
    const t = useT()
    const [name, setName] = useState('')
    const create = useCreateWorkspace()

    return (
        <div className='mx-auto mt-16 max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm'>
            <h1 className='text-lg font-bold text-slate-800 dark:text-slate-100'>
                {t('onboarding.welcome')}
            </h1>
            <p className='mt-1 text-sm text-slate-500 dark:text-slate-400'>
                {t('onboarding.desc')}
            </p>
            <form
                className='mt-4 flex items-end gap-2'
                onSubmit={e => {
                    e.preventDefault()
                    if (!name.trim()) return
                    create.mutate(name.trim(), {
                        onSuccess: () =>
                            toast.success(t('onboarding.created')),
                        onError: err =>
                            toast.error(
                                err instanceof Error
                                    ? err.message
                                    : t('onboarding.createFailed'),
                            ),
                    })
                }}
            >
                <Field label={t('onboarding.name')} className='flex-1'>
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder={t('onboarding.placeholder')}
                        required
                        maxLength={100}
                    />
                </Field>
                <Button type='submit' loading={create.isPending}>
                    {t('common.create')}
                </Button>
            </form>
        </div>
    )
}

/** Anonymes Pageview-Tracking (public, throttled 300 req/min). */
function usePageviews() {
    const location = useLocation()
    useEffect(() => {
        void fetch(`${API_BASE}/pageviews`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: location.pathname }),
            keepalive: true,
        }).catch(() => undefined)
    }, [location.pathname])
}
